import React, { useState } from "react";
import { Box, Button, Avatar, Typography } from "@mui/material";
import packageIcon from "../../assets/packageIcon.png";
import cookingIcon from "../../assets/cookingIcon.png";
import deliverIcon from "../../assets/deliverIcon.png";
import deliveredIcon from "../../assets/deliveredIcon.png";
import { keyframes } from "@mui/system";

const blinkAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

function TrackSectionOrderDetails({ orders = [], onOrderSelect }) {
  // console.log("orders",orders)

  const [trackingId, setTrackingId] = useState("");
  const [trackStatus, setTrackStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleTrackOrder = () => {
    if (!orders || orders.length === 0) {
      setErrorMessage("No orders available for tracking.");
      return;
    }

    const order = orders.find((order) => order.tracking_id === trackingId);

    if (order) {
      const normalizedTrackStatus = order.track_status
        ? order.track_status.trim().replace(/^"|"$/g, "").toLowerCase()
        : ""; // Ensure it's not undefined or null
      setTrackStatus(normalizedTrackStatus);
      setErrorMessage("");
      if (onOrderSelect) {
        onOrderSelect(order); // Safely call onOrderSelect
      }
    } else {
      setTrackStatus(null);
      setErrorMessage("Tracking ID doesn't match any order.");
    }
  };

  const getIconStyles = (status) => {
    const isActive = trackStatus && trackStatus === status.toLowerCase();
    const baseStyles = {
      width: { xs: 40, sm: 54 },
      height: { xs: 40, sm: 54 },
      backgroundColor: "transparent !important",
      boxShadow: "none",
      border: "2px solid #ffffff",
      borderRadius: "50%",
      transition: "all 0.3s ease",
    };

    const activeStyles = isActive
      ? {
          backgroundColor: "#EAEAEA",
          boxShadow: "0px 0px 10px 2px rgb(238, 245, 103)",
          border: "2px solid rgb(238, 245, 103)",
          animation: `${blinkAnimation} 1s infinite ease-in-out`,
        }
      : {};

    return { ...baseStyles, ...activeStyles };
  };

  return (
    <>
      {/* Input for Tracking ID */}
      <form style={{ position: "relative", width: "100%" }}>
        <input
          type="text"
          placeholder="Enter Tracking Id: "
          className="track__order--input"
          value={trackingId}

          onChange={(e) => setTrackingId(e.target.value)}
          style={{
            width: "100%",
            padding: "0.5rem 1rem",
            borderRadius: "1.25rem",
            border: "1px solid #ccc",
            outline: "none",
          }}
        />
        <Button
          variant="contained"
          className="responsive_fontsize16"
          sx={{
            position: "absolute",
            top: "50%",
            right: 0,
            transform: "translateY(-50%)",
            backgroundColor: "#e2ad30",
            textTransform: "capitalize",
            borderRadius: "1.25rem",
            px: { xs: "0.75rem", sm: "1.2rem" }, // Adjusted padding for mobile
            color: "#252920",
            height: "100%", // Adjusted height for mobile
            minWidth: { xs: "20%", sm: "auto" }, // Adjusted width for mobile
            fontFamily:"Inter"
            ,"&:hover": { backgroundColor: "#b58519" }
          }}
          onClick={handleTrackOrder}
        >
          Track Order
        </Button>
      </form>

      {/* Error Message */}
      {errorMessage && (
        <Typography color="error" variant="body1" sx={{ marginTop: "1rem" }}>
          {errorMessage}
        </Typography>
      )}

      {/* Icons with Conditional Highlight */}

      <Box
        sx={{
          position: "relative",
          mt: "1rem",
          display: "flex",
          flexDirection: { xs: "row", sm: "row" },
          alignItems: "center",
          justifyContent: "space-between", // Space out the icons
          gap: { xs: "0", sm: "0" }, // Spacing for vertical layout
          width: "100%",
        }}
      >
        {/* Cooking Icon */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar
            alt="Cooking"
            src={cookingIcon}
            sx={getIconStyles("cooking")}
            imgProps={{ loading: "lazy", width: 54, height: 54 }}
          />
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            Cooking
          </Typography>
        </Box>

        {/* Line between icons */}
        <Box
          sx={{
            width: { xs: "30%", sm: "100%" },
            height: "0.1875rem",
            background: "#D9D9D9",
          }}
        ></Box>

        {/* Packing Icon */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar
            alt="Packing"
            src={packageIcon}
            sx={getIconStyles("packing")}
            imgProps={{ loading: "lazy", width: 54, height: 54 }}
          />
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            Packing
          </Typography>
        </Box>

        {/* Line between icons */}
        <Box
          sx={{
            width: { xs: "30%", sm: "100%" },
            height: "0.1875rem",
            background: "#D9D9D9",
          }}
        ></Box>

        {/* On the Way Icon */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar
            alt="On the way"
            src={deliverIcon}
            sx={getIconStyles("on the way")}
            imgProps={{ loading: "lazy", width: 54, height: 54 }}
          />
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            On the way
          </Typography>
        </Box>

        {/* Line between icons */}
        <Box
          sx={{
            width: { xs: "30%", sm: "100%" },
            height: "0.1875rem",
            background: "#D9D9D9",
          }}
        ></Box>

        {/* Delivered Icon */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar
            alt="Delivered"
            src={deliveredIcon}
            sx={getIconStyles("delivered")}
            imgProps={{ loading: "lazy", width: 54, height: 54 }}
          />
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            Delivered
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default TrackSectionOrderDetails;
